import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Award, CheckCircle, Play } from 'lucide-react';
import { Button } from '../shared/Button';

export const Hero: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleScrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <section className="section-shell relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(24,78,74,0.12),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(231,111,81,0.14),transparent_32%)]" />

      <div className="section-container relative pt-16 md:pt-20">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="section-eyebrow">
              {t('landing.hero.eyebrow')}
            </span>

            <h1 className="mt-6 text-5xl font-bold text-ink md:text-6xl lg:text-7xl">
              {t('landing.hero.title').split(t('landing.hero.titleHighlight'))[0]}{' '}
              <span className="text-primary">{t('landing.hero.titleHighlight')}</span>{' '}
              {t('landing.hero.title').split(t('landing.hero.titleHighlight'))[1]}
            </h1>

            <p className="mt-4 text-base font-semibold text-secondary md:text-lg">
              {t('landing.hero.examYear')}
            </p>

            <p className="mt-5 max-w-2xl text-xl text-slate-600 md:text-2xl">
              {t('landing.hero.subtitle').split(t('landing.hero.subtitleHighlight'))[0]}
              <span className="font-semibold text-primary"> {t('landing.hero.subtitleHighlight')}</span>
            </p>

            <div className="mt-7 flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle className="w-5 h-5 text-success" />
                </div>
                <span className="text-slate-700">{t('landing.hero.trustBadges.exams')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary/10">
                  <Award className="w-5 h-5 text-secondary" />
                </div>
                <span className="text-slate-700">{t('landing.hero.trustBadges.official')}</span>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-500">
              {t('common.independenceDisclaimer')}
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Button
                variant="primary"
                size="large"
                icon={<ArrowRight className="h-5 w-5" />}
                onClick={() => navigate('/try-free')}
              >
                {t('landing.hero.ctaPrimary')}
              </Button>
              <Button
                variant="secondary"
                size="large"
                icon={<Play className="w-5 h-5" />}
                onClick={handleScrollToPricing}
              >
                {t('header.pricing')}
              </Button>
            </div>

            <p className="mt-6 text-sm text-slate-500">
              {t('landing.hero.microTrust1')} &nbsp;·&nbsp; {t('landing.hero.microTrust2')} &nbsp;·&nbsp; {t('landing.hero.microTrust3')}
            </p>
          </div>

          <div className="surface-card-soft hidden p-6 lg:block">
            <div className="mb-4 flex items-center justify-between border-b border-primary/10 pb-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">{t('header.sections.reading')} - {t('exam.reading.question')} 12/36</p>
                <p className="text-xs text-slate-500">{t('examSelection.program')} II</p>
              </div>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                45:23
              </span>
            </div>
            <p className="rounded-xl border border-primary/10 bg-white p-4 text-sm text-slate-600">
              {t('exam.reading.textPlaceholder').substring(0, 140)}...
            </p>
            <div className="mt-4 space-y-2">
              {['A', 'B', 'C', 'D'].map((letter, index) => (
                <div
                  key={letter}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${index === 1 ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white'}`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${index === 1 ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}>
                    {letter}
                  </span>
                  <span className="h-2 flex-1 rounded-full bg-slate-200" />
                </div>
              ))}
            </div>
            <div className="mt-5">
              <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                <span>{t('common.continue')}</span>
                <span>33%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full w-1/3 rounded-full bg-primary" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
